let cachedAudioUrl: string | null = null;
let lastPlayedAt = 0;
const COOLDOWN_MS = 3000;

function writeString(view: DataView, offset: number, str: string): void {
    for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
    }
}

function audioBufferToWav(buffer: AudioBuffer): ArrayBuffer {
    const numChannels = 1;
    const sampleRate = buffer.sampleRate;
    const bitDepth = 16;
    const bytesPerSample = bitDepth / 8;
    const data = buffer.getChannelData(0);
    const dataLength = data.length * bytesPerSample;
    const totalLength = 44 + dataLength;

    const arrayBuffer = new ArrayBuffer(totalLength);
    const view = new DataView(arrayBuffer);

    writeString(view, 0, "RIFF");
    view.setUint32(4, totalLength - 8, true);
    writeString(view, 8, "WAVE");
    writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
    view.setUint16(32, numChannels * bytesPerSample, true);
    view.setUint16(34, bitDepth, true);
    writeString(view, 36, "data");
    view.setUint32(40, dataLength, true);

    let offset = 44;
    for (let i = 0; i < data.length; i++) {
        const s = Math.max(-1, Math.min(1, data[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        offset += 2;
    }

    return arrayBuffer;
}

async function ensureCachedAudio(): Promise<string> {
    if (cachedAudioUrl) return cachedAudioUrl;

    const sampleRate = 44100;
    const duration = 1.4;
    const ctx = new OfflineAudioContext(1, sampleRate * duration, sampleRate);

    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 4000;
    filter.Q.value = 1;
    filter.connect(ctx.destination);

    const playNote = (freq: number, startAt: number, dur: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        osc.connect(gain);
        gain.connect(filter);
        gain.gain.setValueAtTime(0, startAt);
        gain.gain.linearRampToValueAtTime(0.22, startAt + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, startAt + dur);
        osc.start(startAt);
        osc.stop(startAt + dur + 0.05);
    };

    playNote(523.25, 0, 0.6);
    playNote(659.25, 0.2, 0.6);
    playNote(783.99, 0.4, 0.85);

    const rendered = await ctx.startRendering();
    const wav = audioBufferToWav(rendered);
    const blob = new Blob([wav], { type: "audio/wav" });
    cachedAudioUrl = URL.createObjectURL(blob);
    return cachedAudioUrl;
}

export async function playNotificationSound(): Promise<void> {
    const now = Date.now();
    if (now - lastPlayedAt < COOLDOWN_MS) return;
    lastPlayedAt = now;

    try {
        const url = await ensureCachedAudio();
        const audio = new Audio(url);
        audio.volume = 0.5;
        await audio.play();
    } catch {
        // Fallback: direct Web Audio API (works when tab is active)
        try {
            const ctx = new AudioContext();
            if (ctx.state === "suspended") await ctx.resume();

            const filter = ctx.createBiquadFilter();
            filter.type = "lowpass";
            filter.frequency.value = 4000;
            filter.Q.value = 1;
            filter.connect(ctx.destination);

            const playNote = (freq: number, startAt: number, dur: number) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = "sine";
                osc.frequency.value = freq;
                osc.connect(gain);
                gain.connect(filter);
                const t0 = ctx.currentTime + startAt;
                gain.gain.setValueAtTime(0, t0);
                gain.gain.linearRampToValueAtTime(0.22, t0 + 0.015);
                gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
                osc.start(t0);
                osc.stop(t0 + dur + 0.05);
            };

            playNote(523.25, 0, 0.6);
            playNote(659.25, 0.2, 0.6);
            playNote(783.99, 0.4, 0.85);

            // Close the context after the chime finishes to free resources
            setTimeout(() => ctx.close().catch(() => {}), 2000);
        } catch {
            /* Audio not available */
        }
    }
}
